import { Alert, theme, Typography } from 'antd';
import { Column } from '@ant-design/charts';
import type { CSSProperties } from 'react';
import { Permission, ESTADO_CUENTA_SERIE_MENSUAL } from '@aid/shared';
import type { EstadoCuentaSerieMensualBucket, EstadoCuentaSerieMensualResponse } from '@aid/shared';
import { EmptyState } from '../../../components/EmptyState';
import { DashboardCard } from '../../../components/DashboardCard';
import { useHasPermission } from '../../../auth/useHasPermission';
import { formatCurrency, getCurrencySymbol } from '../../../lib/format';
import { monthAxisLabel, formatYAxisCompact, tooltipRowHTML } from '../../../lib/chart-utils';
import { useT } from '../../../locales';
import { useClienteSerieMensual } from '../api/useClientes';
import './cliente-serie-mensual.css';

const { Text } = Typography;

interface ClienteSerieMensualChartProps {
  clienteId: number;
}

interface MonthlyChartDatum {
  mes: string;
  mesLabel: string;
  serie: string;
  value: number;
}

interface TooltipDatum {
  serie?: string;
  value?: number;
}

interface TooltipItem {
  data?: TooltipDatum;
  serie?: string;
  name?: string;
  value?: number;
}

interface TooltipContext {
  title: string;
  items: TooltipItem[];
}

interface SummaryMetricProps {
  label: string;
  value: string;
  color: string;
  testId: string;
}

function safeAmount(value: string | number | undefined): number {
  const parsed = Number.parseFloat(String(value ?? 0));
  return Number.isFinite(parsed) ? Math.max(parsed, 0) : 0;
}

function tooltipSeries(item: TooltipItem): string {
  return item.data?.serie ?? item.serie ?? item.name ?? '';
}

function tooltipValue(item: TooltipItem): number {
  const value = item.data?.value ?? item.value;
  return typeof value === 'number' && Number.isFinite(value) ? Math.max(value, 0) : 0;
}

function SummaryMetric({ label, value, color, testId }: SummaryMetricProps) {
  return (
    <div
      className="cliente-serie-mensual__metric"
      data-testid={testId}
      role="group"
      aria-label={`${label}: ${value}`}
    >
      <div className="cliente-serie-mensual__metric-label">
        <span
          className="cliente-serie-mensual__metric-dot"
          aria-hidden
          style={{ backgroundColor: color }}
        />
        <Text type="secondary">{label}</Text>
      </div>
      <Text
        strong
        style={{ color, fontSize: 18, lineHeight: 1.2, fontVariantNumeric: 'tabular-nums' }}
      >
        {value}
      </Text>
    </div>
  );
}

function hasFinancialData(
  response: EstadoCuentaSerieMensualResponse | undefined,
  serie: EstadoCuentaSerieMensualBucket[],
): boolean {
  return (
    serie.some(
      (bucket) =>
        safeAmount(bucket.facturado) > 0 ||
        safeAmount(bucket.pagado) > 0 ||
        safeAmount(bucket.deudaPendiente) > 0 ||
        safeAmount(bucket.saldoAFavor) > 0,
    ) ||
    safeAmount(response?.balanceInicial.deudaPendiente) > 0 ||
    safeAmount(response?.balanceInicial.saldoAFavor) > 0 ||
    safeAmount(response?.balanceFinal.deudaPendiente) > 0 ||
    safeAmount(response?.balanceFinal.saldoAFavor) > 0
  );
}

function periodMonthLabel(value: string): string {
  return monthAxisLabel(value.slice(0, 7));
}

function tableMoney(value: string): string {
  return formatCurrency(value);
}

export function ClienteSerieMensualChart({ clienteId }: ClienteSerieMensualChartProps) {
  const t = useT();
  const { token } = theme.useToken();
  const symbol = getCurrencySymbol();
  const canReadFinancialHistory = useHasPermission(Permission.ESTADO_CUENTA_READ);
  const monthlyQuery = useClienteSerieMensual(
    clienteId,
    { meses: ESTADO_CUENTA_SERIE_MENSUAL.DEFAULT_MONTHS },
    { enabled: canReadFinancialHistory },
  );

  const response = monthlyQuery.data;
  const serie = response?.data ?? [];
  const isLoading = canReadFinancialHistory && monthlyQuery.isLoading;
  const facturadoLabel = t('pages.clientes.chart.serieFacturado');
  const pagadoLabel = t('pages.clientes.chart.seriePagado');
  const deudaLabel = t('pages.clientes.chart.serieDeudaPendiente');
  const saldoAFavorLabel = t('pages.clientes.chart.serieSaldoAFavor');
  const flowColors = [token.colorPrimary, token.colorSuccess];
  const balanceColors = [token.colorWarning, token.colorInfo];
  const labelMes = monthAxisLabel;
  const flowData: MonthlyChartDatum[] = serie.flatMap((bucket) => [
    {
      mes: bucket.mes,
      mesLabel: labelMes(bucket.mes),
      serie: facturadoLabel,
      value: safeAmount(bucket.facturado),
    },
    {
      mes: bucket.mes,
      mesLabel: labelMes(bucket.mes),
      serie: pagadoLabel,
      value: safeAmount(bucket.pagado),
    },
  ]);
  const balanceData: MonthlyChartDatum[] = serie.flatMap((bucket) => [
    {
      mes: bucket.mes,
      mesLabel: labelMes(bucket.mes),
      serie: deudaLabel,
      value: safeAmount(bucket.deudaPendiente),
    },
    {
      mes: bucket.mes,
      mesLabel: labelMes(bucket.mes),
      serie: saldoAFavorLabel,
      value: safeAmount(bucket.saldoAFavor),
    },
  ]);
  const totalFacturado = serie.reduce((total, bucket) => total + safeAmount(bucket.facturado), 0);
  const totalPagado = serie.reduce((total, bucket) => total + safeAmount(bucket.pagado), 0);
  const balanceFinal = response?.balanceFinal ?? {
    deudaPendiente: '0.00',
    saldoAFavor: '0.00',
  };
  const periodDescription = response?.periodo
    ? t('pages.clientes.chart.resumenPeriodo', {
        meses: response.periodo.meses,
        desde: periodMonthLabel(response.periodo.fechaDesde),
        hasta: periodMonthLabel(response.periodo.fechaHasta),
      })
    : t('pages.clientes.chart.resumenPeriodoCorto', {
        meses: ESTADO_CUENTA_SERIE_MENSUAL.DEFAULT_MONTHS,
      });
  const chartStyle = {
    '--cliente-serie-border': token.colorBorderSecondary,
    '--cliente-serie-muted': token.colorTextSecondary,
  } as CSSProperties;

  const flowTooltipRender = (_event: unknown, context: TooltipContext) => {
    const bySerie = new Map(context.items.map((item) => [tooltipSeries(item), tooltipValue(item)]));
    return `
      <div style="min-width:200px;margin:-12px;padding:12px;background:${token.colorBgElevated};border-radius:4px;">
        <div style="font-weight:600;font-size:12px;color:${token.colorText};margin-bottom:6px;">${context.title}</div>
        ${tooltipRowHTML(facturadoLabel, bySerie.get(facturadoLabel) ?? 0, flowColors[0]!, token)}
        ${tooltipRowHTML(pagadoLabel, bySerie.get(pagadoLabel) ?? 0, flowColors[1]!, token)}
      </div>`;
  };

  const balanceTooltipRender = (_event: unknown, context: TooltipContext) => {
    const bySerie = new Map(context.items.map((item) => [tooltipSeries(item), tooltipValue(item)]));
    return `
      <div style="min-width:210px;margin:-12px;padding:12px;background:${token.colorBgElevated};border-radius:4px;">
        <div style="font-weight:600;font-size:12px;color:${token.colorText};margin-bottom:6px;">${context.title}</div>
        ${tooltipRowHTML(deudaLabel, bySerie.get(deudaLabel) ?? 0, balanceColors[0]!, token)}
        ${tooltipRowHTML(saldoAFavorLabel, bySerie.get(saldoAFavorLabel) ?? 0, balanceColors[1]!, token)}
      </div>`;
  };

  return (
    <DashboardCard
      style={{ height: '100%' }}
      bodyStyle={{ padding: '18px 22px 24px' }}
      title={t('pages.clientes.chart.title')}
      subtitle={t('pages.clientes.chart.subtitle')}
      loading={isLoading}
    >
      {!canReadFinancialHistory ? (
        <EmptyState
          compact
          title={t('pages.clientes.chart.permissionTitle')}
          description={t('pages.clientes.chart.permissionDesc')}
        />
      ) : monthlyQuery.isError ? (
        <Alert
          showIcon
          type="error"
          message={t('pages.clientes.chart.errorTitle')}
          description={t('pages.clientes.chart.errorDesc')}
        />
      ) : !hasFinancialData(response, serie) ? (
        <EmptyState
          compact
          title={t('pages.clientes.chart.sinDatos')}
          description={t('pages.clientes.chart.sinDatosDesc', {
            meses: response?.periodo.meses ?? ESTADO_CUENTA_SERIE_MENSUAL.DEFAULT_MONTHS,
          })}
        />
      ) : (
        <div className="cliente-serie-mensual" style={chartStyle}>
          <div
            className="cliente-serie-mensual__summary"
            aria-label={periodDescription}
            data-testid="cliente-serie-mensual-summary"
          >
            <Text type="secondary" className="cliente-serie-mensual__period">
              {periodDescription}
            </Text>
            <div className="cliente-serie-mensual__metrics">
              <SummaryMetric
                label={facturadoLabel}
                value={formatCurrency(totalFacturado.toFixed(2))}
                color={flowColors[0]!}
                testId="cliente-serie-mensual-facturado"
              />
              <SummaryMetric
                label={pagadoLabel}
                value={formatCurrency(totalPagado.toFixed(2))}
                color={flowColors[1]!}
                testId="cliente-serie-mensual-pagado"
              />
              <SummaryMetric
                label={deudaLabel}
                value={formatCurrency(balanceFinal.deudaPendiente)}
                color={balanceColors[0]!}
                testId="cliente-serie-mensual-deuda"
              />
              <SummaryMetric
                label={saldoAFavorLabel}
                value={formatCurrency(balanceFinal.saldoAFavor)}
                color={balanceColors[1]!}
                testId="cliente-serie-mensual-saldo-a-favor"
              />
            </div>
          </div>

          <div className="cliente-serie-mensual__visuals">
            <section
              className="cliente-serie-mensual__visual"
              aria-label={t('pages.clientes.chart.flujosSection')}
            >
              <div className="cliente-serie-mensual__section-heading">
                <Text strong>{t('pages.clientes.chart.flujosSection')}</Text>
                <Text type="secondary">{t('pages.clientes.chart.flujosSectionDesc')}</Text>
              </div>
              <div
                role="img"
                aria-label={t('pages.clientes.chart.flujosAria')}
                className="cliente-serie-mensual__chart"
              >
                <Column
                  data={flowData}
                  xField="mesLabel"
                  yField="value"
                  colorField="serie"
                  group
                  color={flowColors}
                  scale={{
                    color: {
                      domain: [facturadoLabel, pagadoLabel],
                      range: flowColors,
                    },
                  }}
                  height={240}
                  axis={{
                    y: {
                      labelFormatter: (value: number) => formatYAxisCompact(value, symbol),
                      style: {
                        labelFill: token.colorTextSecondary,
                        gridStroke: token.colorSplit,
                        lineStroke: token.colorBorderSecondary,
                      },
                    },
                    x: {
                      style: {
                        labelFill: token.colorTextSecondary,
                        lineStroke: token.colorBorderSecondary,
                        tickStroke: token.colorBorderSecondary,
                      },
                    },
                  }}
                  legend={{
                    color: {
                      position: 'top',
                      layout: { justifyContent: 'center' },
                      itemLabelFill: token.colorTextSecondary,
                    },
                  }}
                  tooltip={{
                    title: (datum: { mesLabel: string }) => datum.mesLabel,
                  }}
                  interaction={{ tooltip: { render: flowTooltipRender } }}
                />
              </div>
            </section>

            <section
              className="cliente-serie-mensual__visual"
              aria-label={t('pages.clientes.chart.saldosSection')}
            >
              <div className="cliente-serie-mensual__section-heading">
                <Text strong>{t('pages.clientes.chart.saldosSection')}</Text>
                <Text type="secondary">{t('pages.clientes.chart.saldosSectionDesc')}</Text>
              </div>
              <div
                role="img"
                aria-label={t('pages.clientes.chart.saldosAria')}
                className="cliente-serie-mensual__chart"
              >
                <Column
                  data={balanceData}
                  xField="mesLabel"
                  yField="value"
                  colorField="serie"
                  group
                  color={balanceColors}
                  scale={{
                    color: {
                      domain: [deudaLabel, saldoAFavorLabel],
                      range: balanceColors,
                    },
                  }}
                  height={240}
                  axis={{
                    y: {
                      labelFormatter: (value: number) => formatYAxisCompact(value, symbol),
                      style: {
                        labelFill: token.colorTextSecondary,
                        gridStroke: token.colorSplit,
                        lineStroke: token.colorBorderSecondary,
                      },
                    },
                    x: {
                      style: {
                        labelFill: token.colorTextSecondary,
                        lineStroke: token.colorBorderSecondary,
                        tickStroke: token.colorBorderSecondary,
                      },
                    },
                  }}
                  legend={{
                    color: {
                      position: 'top',
                      layout: { justifyContent: 'center' },
                      itemLabelFill: token.colorTextSecondary,
                    },
                  }}
                  tooltip={{
                    title: (datum: { mesLabel: string }) => datum.mesLabel,
                  }}
                  interaction={{ tooltip: { render: balanceTooltipRender } }}
                />
              </div>
            </section>
          </div>

          <details className="cliente-serie-mensual__data">
            <summary>{t('pages.clientes.chart.dataTableTitle')}</summary>
            <div className="cliente-serie-mensual__data-scroll">
              <table aria-label={t('pages.clientes.chart.dataTableAria')}>
                <thead>
                  <tr>
                    <th scope="col">{t('pages.clientes.chart.dataMonth')}</th>
                    <th scope="col">{facturadoLabel}</th>
                    <th scope="col">{pagadoLabel}</th>
                    <th scope="col">{deudaLabel}</th>
                    <th scope="col">{saldoAFavorLabel}</th>
                  </tr>
                </thead>
                <tbody>
                  {serie.map((bucket) => (
                    <tr key={bucket.mes}>
                      <th scope="row">{labelMes(bucket.mes)}</th>
                      <td>{tableMoney(bucket.facturado)}</td>
                      <td>{tableMoney(bucket.pagado)}</td>
                      <td>{tableMoney(bucket.deudaPendiente)}</td>
                      <td>{tableMoney(bucket.saldoAFavor)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        </div>
      )}
    </DashboardCard>
  );
}
